import {
  listHardwareMics,
  type HardwareMicDevice,
  type HardwareMicModel,
} from '@/tauri/dictation/api';
import { isTauri } from '@/tauri/utils';
import type { AddDeviceListItem } from '@heidi/dictate';
import {
  dictateAddDeviceDialogRequestAtom,
  hardwareMicDeviceKey,
  hardwareMicDevicesAtom,
} from '@heidi/dictate/data';
import { atom, getDefaultStore } from 'jotai';
/** Pretty names follow the dev picker's model segments. */
const MODEL_LABELS: Record<HardwareMicModel, string> = {
  power_mic_4: 'PowerMic 4',
  power_mic_3: 'PowerMic III',
  speech_mike: 'SpeechMike',
  rec_mic: 'RecMic',
};

const toListItem = (d: HardwareMicDevice): AddDeviceListItem => ({
  key: hardwareMicDeviceKey(d),
  model: d.model,
  displayName: d.product ?? MODEL_LABELS[d.model],
});

export type AddDeviceDevPreview = 'searching' | 'one' | 'many';

/** Non-prod dev tools set this so the dialog opens on a chosen state. */
export const addDeviceDialogDevPreviewAtom = atom<{
  state: AddDeviceDevPreview;
  model: string;
} | null>(null);

const previewStore = getDefaultStore();
previewStore.sub(dictateAddDeviceDialogRequestAtom, () => {
  if (previewStore.get(dictateAddDeviceDialogRequestAtom) === null) {
    previewStore.set(addDeviceDialogDevPreviewAtom, null);
  }
});

const previewItem = (model: string, serial: string): AddDeviceListItem => ({
  key: `${model}:1364:100:${serial}`,
  model,
  displayName: MODEL_LABELS[model as HardwareMicModel] ?? model,
});

const previewList = (preview: {
  state: AddDeviceDevPreview;
  model: string;
}): Promise<AddDeviceListItem[]> => {
  if (preview.state === 'searching')
    return new Promise<AddDeviceListItem[]>(() => undefined);
  const primary = previewItem(preview.model, 'dev-preview');
  if (preview.state === 'one') return Promise.resolve([primary]);
  const other = preview.model === 'speech_mike' ? 'power_mic_4' : 'speech_mike';
  return Promise.resolve([primary, previewItem(other, 'dev-preview-2')]);
};

/**
 * The add-device dialog's `listDevices` service. On Tauri, refresh the HID
 * reader's snapshot and keep any connect the listener already recorded. The
 * listener owns `hardwareMicDevicesAtom` — writing the snapshot back would
 * resurrect a device that unplugged while this list was in flight.
 */
export const listAddDevices = async (): Promise<AddDeviceListItem[]> => {
  const store = getDefaultStore();
  const preview = store.get(addDeviceDialogDevPreviewAtom);
  if (preview) {
    if (preview.state !== 'searching') store.set(addDeviceDialogDevPreviewAtom, null);
    return previewList(preview);
  }
  if (!isTauri()) {
    return store.get(hardwareMicDevicesAtom).map(toListItem);
  }
  const snapshot = await listHardwareMics();
  const seen = new Set(snapshot.map(hardwareMicDeviceKey));
  const kept = store
    .get(hardwareMicDevicesAtom)
    .filter((device) => !seen.has(hardwareMicDeviceKey(device)));
  return [...snapshot, ...kept].map(toListItem);
};
